import { useCallback, useEffect, useRef, useState } from "react";
import { useNotificationFrames, useNotificationsConnection } from "@weco/suite-chrome";
import { videoService } from "../api/video.api";
import type { DirectCallInvite } from "../api/types/video";
import { isRingExpired, ringRemainingMs } from "../lib/ringExpiry";
import { useWorkerInterval } from "./useWorkerInterval";

const POLL_INTERVAL_MS = 4000;

/**
 * Incoming 1:1 direct-call invites (see services/video's
 * DirectCallViewSet.pending).
 *
 * Push first: the Notification Service's socket delivers `call.incoming` the
 * moment a call is placed and `retracted` the moment it is answered, declined
 * or cancelled anywhere. The frame only says *that* something changed — the
 * invite itself (caller, room, `created_at` for expiry) is always read from
 * the pending endpoint, so there is one source of truth for what rings.
 *
 * The 4 s REST poll stays as the fallback, and runs only while that socket is
 * not connected (spec §6): a tab whose socket is down — service unreachable,
 * reconnecting, not deployed — rings exactly as it always did.
 *
 * `enabled` should be false while unauthenticated or already in a call —
 * the hook still runs (rules of hooks) but skips network requests and
 * returns null.
 *
 * The invite also expires *client-side* at `RING_TIMEOUT_MS` (GH-1600 D4).
 * Polling alone cannot end a ring: a failed poll leaves the last invite
 * standing — correctly, since a two-second blip must not drop a live call —
 * so a connection that never comes back would otherwise ring until the page
 * was reloaded. Expiry is therefore driven off the invite's own `created_at`
 * and needs no network at all.
 */
export function useIncomingCalls(enabled: boolean): DirectCallInvite | null {
  const [invite, setInvite] = useState<DirectCallInvite | null>(null);
  const fetchingRef = useRef(false);
  // When this tab first saw the current invite — the clock-skew fallback in
  // ringExpiry. One slot, because only one invite is ever displayed; keeping it
  // across a clear is what stops an expired invite the server is still serving
  // from flapping back into view on the next poll.
  const firstSeenRef = useRef<{ id: string; at: number } | null>(null);
  // Live socket ⇒ no polling. Read from the worker tick through a ref so the
  // ticker is not torn down and re-armed on every connection change.
  const connection = useNotificationsConnection(enabled);
  const socketLive = enabled && connection === "connected";
  const socketLiveRef = useRef(socketLive);
  socketLiveRef.current = socketLive;
  const inviteRef = useRef<DirectCallInvite | null>(null);
  inviteRef.current = invite;
  // `call.incoming` notification id → the invite it announced, so a
  // `retracted` frame can end the right ring without waiting for a request.
  const ringNotificationsRef = useRef(new Map<string, string | null>());
  const rerunRef = useRef(false);

  const firstSeenAt = useCallback((id: string, now: number): number => {
    if (firstSeenRef.current?.id !== id) {
      firstSeenRef.current = { id, at: now };
    }
    return firstSeenRef.current.at;
  }, []);

  /** Drop the invite if its ring window has closed. Never touches the network. */
  const expireIfElapsed = useCallback(() => {
    const now = Date.now();
    setInvite((current) => {
      if (!current) return current;
      // Read-only: the updater can run twice under StrictMode, so the ref is
      // recorded in the poll path and only consulted here.
      const seen = firstSeenRef.current?.id === current.id ? firstSeenRef.current.at : now;
      return isRingExpired(current.created_at, now, seen) ? null : current;
    });
  }, []);

  const poll = useCallback(async () => {
    if (!enabled || fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const pending = await videoService.getPendingDirectCalls();
      const next = pending[0] ?? null;
      if (!next) {
        setInvite(null);
        return;
      }
      const now = Date.now();
      // An invite that is already past its window must never ring, even for the
      // moment before the next sweep — the server is not supposed to serve one,
      // so this is the client's own fail-safe rather than a routine path.
      setInvite(isRingExpired(next.created_at, now, firstSeenAt(next.id, now)) ? null : next);
    } catch (err) {
      // Deliberately leaves `invite` alone: a poll failure is not evidence the
      // call ended, and dropping the banner on a blip would hang up on the
      // caller. The ring still ends — expiry above is what ends it.
      console.error("useIncomingCalls: poll failed:", err);
    } finally {
      fetchingRef.current = false;
      if (rerunRef.current) {
        rerunRef.current = false;
        queueMicrotask(() => void pollRef.current());
      }
    }
  }, [enabled, firstSeenAt]);
  const pollRef = useRef(poll);
  pollRef.current = poll;

  /**
   * Poll on a push signal. Unlike the timer, a signal must not be dropped
   * because a request is already in flight — that request may have left
   * before the invite existed, and with the socket live no timer follows.
   */
  const pollSoon = useCallback(() => {
    if (fetchingRef.current) {
      rerunRef.current = true;
      return;
    }
    void pollRef.current();
  }, []);

  useEffect(() => {
    if (!enabled) {
      setInvite(null);
      firstSeenRef.current = null;
      return;
    }
    // Poll once immediately so a call placed while this mounts is picked up
    // without waiting a full period.
    void poll();
  }, [enabled, poll]);

  // Either edge of the socket's state is a reason to look: coming up, to catch
  // anything rung during the gap; going down, so the fallback starts at once
  // rather than one period later.
  const prevLiveRef = useRef(socketLive);
  useEffect(() => {
    if (prevLiveRef.current === socketLive) return;
    prevLiveRef.current = socketLive;
    if (enabled) pollSoon();
  }, [socketLive, enabled, pollSoon]);

  useNotificationFrames((frame) => {
    if (frame.type === "notification") {
      if (frame.category !== "calls" && !frame.kind.startsWith("call.")) return;
      if (frame.kind === "call.incoming") {
        const inviteId = typeof frame.data?.invite_id === "string" ? frame.data.invite_id : null;
        ringNotificationsRef.current.set(frame.id, inviteId);
      }
      pollSoon();
      return;
    }
    if (frame.type === "retracted") {
      const ended = (frame.ids ?? []).filter((id) => ringNotificationsRef.current.has(id));
      if (ended.length > 0) {
        const endedInvites = new Set(ended.map((id) => ringNotificationsRef.current.get(id)));
        for (const id of ended) ringNotificationsRef.current.delete(id);
        // The ring ended elsewhere: stop it now rather than after a round
        // trip. A retraction that names no invite ends whichever is showing.
        setInvite((current) =>
          current && (endedInvites.has(current.id) || endedInvites.has(null)) ? null : current,
        );
      }
      // Confirm against the server — another invite may still be pending,
      // and a ring this tab learned about by polling has no mapping above.
      if (ended.length > 0 || inviteRef.current) pollSoon();
    }
  }, enabled);

  // Expire on the exact millisecond the window closes, rather than whenever the
  // next tick happens to land. Re-armed whenever the invite changes.
  useEffect(() => {
    if (!invite) return;
    const now = Date.now();
    const seen = firstSeenRef.current?.id === invite.id ? firstSeenRef.current.at : now;
    const timer = window.setTimeout(
      expireIfElapsed,
      ringRemainingMs(invite.created_at, now, seen),
    );
    return () => window.clearTimeout(timer);
  }, [invite, expireIfElapsed]);

  // Ticked from a Web Worker, not `setInterval`. A hidden tab has its
  // main-thread timers clamped to ~1/minute after 5 minutes, and invites expire
  // after RING_TIMEOUT_MS — so a main-thread poll would miss the whole ring
  // window in precisely the case ringing is for. See ../lib/tickerWorker.ts.
  //
  // The same clamping applies to the timeout above, which is why expiry is
  // swept here too: in a backgrounded tab this tick is the one that fires. The
  // tick keeps running while the socket is live — only the request is skipped.
  useWorkerInterval(
    () => {
      expireIfElapsed();
      if (!socketLiveRef.current) void poll();
    },
    POLL_INTERVAL_MS,
    enabled,
    (reason) => {
      console.warn(
        `useIncomingCalls: worker ticker unavailable (${reason}); falling back to ` +
          "setInterval. Incoming calls may be missed while the tab is backgrounded.",
      );
    },
  );

  return invite;
}
